// Copyright 2026 Hardfin, Inc.
import { LocationName } from '@/components/location-name';
import { LocationOnOutlined } from '@/constants/hardfin-standard-icons';
import { Movement } from '@/movement/api/types';
import {
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { MouseEvent } from 'react';

interface Props {
  movement: Movement;
  isBulkMode: boolean;
  isSelected: boolean;
  onToggleSelect: (movement: Movement) => void;
}

export function MovementTableRow(props: Props) {
  const navigate = useNavigate();
  const router = useRouter();
  const movement = props.movement;
  const isShipmentLinked = movement.shipmentNumber !== null;

  const handleCheckboxChange = () => {
    props.onToggleSelect(movement);
  };

  const handleRowClick = (e: MouseEvent) => {
    if (props.isBulkMode) {
      if (!isShipmentLinked) {
        props.onToggleSelect(movement);
      }
      return;
    }

    const target = {
      to: '/assets/$assetKey' as const,
      params: { assetKey: movement.asset.key },
      search: { editEventGroup: movement.key },
    };

    // Ctrl+click or Cmd+click opens in new tab
    if (e.ctrlKey || e.metaKey) {
      const location = router.buildLocation(target);
      window.open(location.href, '_blank');
      return;
    }

    void navigate(target);
  };

  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={handleRowClick}>
      {props.isBulkMode && (
        <TableCell
          padding="none"
          sx={{ width: 32, textAlign: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          {isShipmentLinked ? (
            <Tooltip
              arrow
              title="Movements that are part of a shipment cannot be modified directly, please update the shipment"
            >
              <span>
                <Checkbox disabled checked={false} size="small" sx={{ p: 0 }} />
              </span>
            </Tooltip>
          ) : (
            <Checkbox
              checked={props.isSelected}
              onChange={handleCheckboxChange}
              size="small"
              sx={{ p: 0 }}
            />
          )}
        </TableCell>
      )}
      <TableCell>
        <Tooltip arrow title={movement.asset.item.name}>
          <div style={{ display: 'inline-block' }}>
            <Typography variant="body2" noWrap>
              {movement.asset.serial}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
              }}
            >
              {movement.asset.item.sku}
            </Typography>
          </div>
        </Tooltip>
      </TableCell>
      <TableCell>
        {movement.originLocation ? (
          <Stack direction="row" alignItems="center" gap={0.3}>
            <LocationOnOutlined
              sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }}
            />
            <LocationName
              location={{
                name: movement.originLocation.name ?? '',
                address: movement.originLocation.address,
              }}
              showAddress="tooltip"
              variant="body2"
              noWrap
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" noWrap>
          {movement.shippedAt
            ? movement.shippedAt.toFormat('MMM d, yyyy')
            : '-'}
        </Typography>
      </TableCell>
      <TableCell>
        {movement.destinationLocation ? (
          <Stack direction="row" alignItems="center" gap={0.3}>
            <LocationOnOutlined
              sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }}
            />
            <LocationName
              location={{
                name: movement.destinationLocation.name ?? '',
                address: movement.destinationLocation.address,
              }}
              showAddress="tooltip"
              variant="body2"
              noWrap
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" noWrap>
          {movement.deliveredAt
            ? movement.deliveredAt.toFormat('MMM d, yyyy')
            : '-'}
        </Typography>
      </TableCell>
      <TableCell>
        {movement.shipmentNumber ? (
          <Typography variant="body2" noWrap>
            {movement.shipmentNumber}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not set
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary" noWrap>
          {movement.lastActivityAt.toFormat('MMM d, yyyy h:mm a')}
        </Typography>
      </TableCell>
    </TableRow>
  );
}
